// src/utils/realApiService.js (minimal live backend client used by apiService)

import { API_BASE_URL } from './frontendConfig.js';
import sessionService from './sessionService.js';


const getAuthHeaders = (extra = {}) => {
  let token;
  let organizationId;

  try {
    token = sessionService?.getSessionToken?.();
    organizationId = sessionService?.getOrganizationId?.();
  } catch {
    // sessionService not ready yet
  }

  const headers = {
    'Content-Type': 'application/json',
    ...extra,
  };

  // Attach token ONLY if available
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  // Attach org context ONLY if available
  if (organizationId) {
    headers['X-Organization-Id'] = organizationId;
  }

  return headers;
};


const fetchWithCredentials = async (url, options = {}) => {
  const organizationId = sessionService?.getOrganizationId?.();

  let finalUrl = url;

  // Append org_id only if not already present
  if (organizationId && !/organization_id=/.test(url)) {
    const sep = url.includes('?') ? '&' : '?';
    finalUrl = `${url}${sep}organization_id=${organizationId}`;
  }

  return fetch(finalUrl, {
    ...options,
    headers: {
      ...getAuthHeaders(),
      ...options.headers,
    },
  });
};


const handle = async (resp) => {
  const text = await resp.text();
  let json;
  try {
    json = text ? JSON.parse(text) : {};
  } catch {
    json = { message: text };
  }
  if (!resp.ok) {
    // Handle authentication errors (401) - clear session and redirect to login
    if (resp.status === 401) {
      // Check if session service is currently initializing
      const isInitializing = sessionService?.isInitializing;
      
      if (isInitializing) {
        console.warn('🔒 401 during initialization - suppressing session purge');
        const error = new Error('Authentication required (initializing)');
        error.status = resp.status;
        throw error;
      }

      console.warn('🔒 Authentication failed - clearing session and redirecting to login');

      // Clear session data
      if (sessionService && typeof sessionService.clearSession === 'function') {
        sessionService.clearSession();
      }

      // Redirect to login page if not already there
      if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
        window.location.href = '/login';
      }

      const error = new Error('Authentication required');
      error.status = resp.status;
      throw error;
    }

    // Handle validation errors (422) with detailed messages
    if (resp.status === 422 && json?.detail) {
      // FastAPI validation errors format
      const validationErrors = Array.isArray(json.detail)
        ? json.detail.map(err => `${err.loc?.join('.')}: ${err.msg}`).join(', ')
        : json.detail;
      const error = new Error(`Validation error: ${validationErrors}`);
      error.status = resp.status;
      throw error;
    }

    // Create error with status code and detailed message
    const msg = json?.detail || json?.error?.message || json?.message || `HTTP ${resp.status}`;
    const error = new Error(msg);
    error.status = resp.status;
    throw error;
  }
  return json;
};

const buildQS = (params = {}) => {
  const usp = new URLSearchParams();
  for (const [k, v] of Object.entries(params))
    if (v !== undefined && v !== null && v !== '') usp.append(k, v);
  const qs = usp.toString();
  return qs ? `?${qs}` : '';
};

const realApiService = {
  // Basic HTTP methods
  get: async (url) => {
    const resp = await fetchWithCredentials(`${API_BASE_URL}${url}`, {
      method: 'GET',
    });
    return handle(resp);
  },

  // Task Assignment and My Tasks
  taskAssignment: {
    getMyAssignedTasks: async (params = {}) => {
      // Handle case where params is just a status string (legacy support)
      const normalizedParams = typeof params === 'string' ? { status: params } : params;
      const qs = buildQS(normalizedParams);
      const url = `${API_BASE_URL}/api/v1/task-assignment/my-assigned-tasks${qs}`;
      const resp = await fetchWithCredentials(url);
      const result = await handle(resp);
      // Normalize to tasks array
      if (Array.isArray(result?.tasks)) return result.tasks;
      if (Array.isArray(result)) return result;
      return [];
    },
    assignTask: async (taskData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/task-assignment/assign-task`, {
        method: 'POST',
        body: JSON.stringify(taskData),
      });
      return handle(resp);
    },
    getTeamAssignments: async (organizationId) => {
      console.log('🔄 API: getTeamAssignments executing for', organizationId);
      // Adding timestamp to force fresh fetch and debug caching issues
      const ts = Date.now();
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/task-assignment/team-assignments/${organizationId}?_t=${ts}`);
      const result = await handle(resp);
      return result;
    },
    acceptTask: async (taskId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/task-assignment/accept-task/${taskId}`, {
        method: 'POST',
      });
      return handle(resp);
    },
  },

  post: async (url, data) => {
    const resp = await fetchWithCredentials(`${API_BASE_URL}${url}`, {
      method: 'POST',
      body: JSON.stringify(data),
    });
    return handle(resp);
  },

  put: async (url, data) => {
    const resp = await fetchWithCredentials(`${API_BASE_URL}${url}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
    return handle(resp);
  },

  delete: async (url) => {
    const resp = await fetchWithCredentials(`${API_BASE_URL}${url}`, {
      method: 'DELETE',
    });
    return handle(resp);
  },

  // Authentication
  auth: {
    register: async (data) => {
      const body = {
        email: data.email,
        password: data.password,
        first_name: data.firstName || data.first_name,
        last_name: data.lastName || data.last_name,
        organization_name: data.organizationName || data.organization_name,
        organization_domain:
          data.organizationDomain || data.organization_domain,
        organization_id: data.organizationId || data.organization_id,
      };
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/auth/register`, {
        method: 'POST',
        body: JSON.stringify(body),
      });
      return handle(resp);
    },
    login: async (email, password) => {
      console.log('🔍 Login attempt:', { email, apiUrl: `${API_BASE_URL}/api/v1/auth/login` });
      try {
        const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/auth/login`, {
          method: 'POST',
          body: JSON.stringify({ email, password }),
        });
        console.log('📡 Login response status:', resp.status);
        const result = await handle(resp);
        console.log('✅ Login successful:', result);
        // Database-only authentication - no localStorage usage
        // Tokens are handled by sessionService
        return result;
      } catch (error) {
        console.error('❌ Login error:', error);
        throw error;
      }
    },
    logout: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/auth/logout`, {
        method: 'POST',
      });
      const result = await handle(resp);
      // Database-only authentication - no localStorage usage
      // Session cleanup is handled by sessionService
      return result;
    },
    changePassword: async (data) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/change-password`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      const result = await handle(resp);
      return { success: true, data: result };
    },
    enable2FA: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/2fa/setup`, {
        method: 'POST',
      });
      return handle(resp);
    },
    verify2FA: async (token) => {
      // Backend expects 'code' as a parameter; pass via query to avoid 422
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/2fa/verify?code=${encodeURIComponent(token)}`, {
        method: 'POST',
      });
      return handle(resp);
    },
  },

  // Users
  users: {
    getCurrentUser: async () => {
      // Corrected endpoint: the user profile with role is under /api/v1/users/me
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/users/me`);
      return handle(resp);
    },
    updateProfile: async (updateData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/users/profile`, {
        method: 'PUT',
        body: JSON.stringify(updateData),
      });
      return handle(resp);
    },

    getActivityStatus: async (organizationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/users/activity-status${buildQS({ organization_id: organizationId })}`
      );
      return handle(resp);
    },
    getSessions: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/sessions`);
      return handle(resp);
    },
    revokeSession: async (sessionId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/sessions/${sessionId}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },
    getSecurityActivity: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/user-security/activity`);
      return handle(resp);
    },

    updateActivity: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/users/activity`, {
        method: 'POST',
      });
      return handle(resp);
    },

  },

  // Dashboard / Analytics (minimal)
  dashboard: {
    getStats: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/analytics/dashboard/stats`
      );
      // Normalize into {data, error}
      try {
        const j = await handle(resp);
        return { data: j.data || j, error: null };
      } catch (e) {
        return { data: null, error: e.message };
      }
    },
  },
  analytics: {
    getUserAnalytics: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/analytics/user`);
      return handle(resp);
    },
    getOrganizationPerformance: async (organizationId, period = '30d') => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/analytics/organizations/${organizationId}/performance${buildQS(
          { period }
        )}`
      );
      return handle(resp);
    },
    getUsageAnalytics: async (period = '30d') => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/analytics/usage${buildQS({ period })}`
      );
      return handle(resp);
    },
    getDashboardStats: async () => realApiService.dashboard.getStats(),
  },

  // Dashboard API (role-specific)
  dashboardApi: {
    getOwnerDashboard: async (organizationId = null) => {
      const qs = buildQS(organizationId ? { organization_id: organizationId } : {});
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/dashboard/owner-dashboard${qs}`);
      return handle(resp);
    },

    getAdminDashboard: async (organizationId) => {
      const qs = organizationId ? `?organization_id=${organizationId}` : '';
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/dashboard/admin-dashboard${qs}`);
      return handle(resp);
    },

    getMemberDashboard: async (organizationId) => {
      const qs = organizationId ? `?organization_id=${organizationId}` : '';
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/dashboard/member-dashboard${qs}`);
      return handle(resp);
    },

    getStats: async (organizationId) => {
      const qs = organizationId ? `?organization_id=${organizationId}` : '';
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/dashboard/stats${qs}`);
      return handle(resp);
    }
  },

  // Organizations (members endpoints used by teamService)
  organizations: {
    getAll: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      // Backend returns a bare list for this endpoint, or wraps it in data
      const organizations = Array.isArray(r) ? r : (r.data || []);
      return {
        organizations: organizations,
      };
    },
    getPublic: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/public`);
      return handle(resp);
    },
    getById: async (id) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${id}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      return r.data || r;
    },
    getMembers: async (organizationId, filters = {}) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/members${buildQS(
          filters
        )}`,
        { headers: getAuthHeaders() }
      );
      const r = await handle(resp);
      // Backend returns array directly, not wrapped in data property
      return Array.isArray(r) ? r : (r.data || []);
    },
    // FIX: Add missing getAcceptedInvitations method
    getAcceptedInvitations: async (organizationId) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/members${buildQS({ status: 'accepted' })}`,
        { headers: getAuthHeaders() }
      );
      const r = await handle(resp);
      return r.data || [];
    },
    // Add method to get pending invitations using the correct backend endpoint
    getPendingInvitations: async (organizationId) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/invitations`,
        { headers: getAuthHeaders() }
      );
      const r = await handle(resp);
      return r.data || r || [];
    },
    create: async (data) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    update: async (id, data) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    getSettings: async (organizationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations-enhanced/${organizationId}/settings`
      );
      return handle(resp);
    },
    updateSettings: async (organizationId, settings) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations-enhanced/${organizationId}/settings`,
        {
          method: 'PUT',
          body: JSON.stringify(settings),
        }
      );
      return handle(resp);
    },
    cancelInvitation: async (organizationId, inviteId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/invitations/${inviteId}`,
        { method: 'DELETE' }
      );
      return handle(resp);
    },
    inviteMember: async (organizationId, inviteData) => {
      const payload = {
        email: inviteData.email,
        role: inviteData.role || 'member',
      };
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/invite`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
      return handle(resp);
    },
    getJoinRequests: async (organizationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/join-requests`
      );
      return handle(resp);
    },
    approveJoinRequest: async (organizationId, registrationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/join-requests/${registrationId}/approve`,
        { method: 'POST' }
      );
      return handle(resp);
    },
    rejectJoinRequest: async (organizationId, registrationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/join-requests/${registrationId}/reject`,
        { method: 'POST' }
      );
      return handle(resp);
    },
    acceptInvitation: async (invitationData) => {
      // Don't use fetchWithCredentials for invitation acceptance since user doesn't have account yet
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/accept-invitation`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            // Explicitly don't include Authorization header
          },
          body: JSON.stringify(invitationData),
        }
      );
      return handle(resp);
    },
    verifyInvitation: async (token) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/verify-invitation/${token}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return handle(resp);
    },
    getActivities: async (organizationId, params = {}) => {
      const days = params?.days ?? 7;
      const qs = buildQS({ organization_id: organizationId, days });
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/metrics/activity/summary${qs}`);
      const result = await handle(resp);
      return Array.isArray(result?.activities) ? result.activities : [];
    },
    initializeDashboard: async (organizationId) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/initialize-dashboard`,
        {
          method: 'POST',
          headers: getAuthHeaders()
        }
      );
      return handle(resp);
    },
  },

  // Projects
  projects: {
    getAll: async (organizationId) => {
      // Backend supports organization_id as a query parameter
      const qs = buildQS(
        organizationId ? { organization_id: organizationId } : {}
      );
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects${qs}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      // Backend returns a bare list for this endpoint
      return r;
    },
    getById: async (id) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${id}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      // Backend returns a single project object
      return r;
    },
    getStats: async (projectId, organizationId) => {
      const qs = buildQS({ organization_id: organizationId });
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/stats${qs}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      return r;
    },
    getTeamMembers: async (projectId, organizationId) => {
      const qs = buildQS({ organization_id: organizationId, _t: Date.now() });
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/team-members${qs}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      return r;
    },
    addTeamMember: async (projectId, memberData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/team-members`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(memberData),
      });
      return handle(resp);
    },
    invite: async (projectId, inviteData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/projects/${projectId}/invite`, {
        method: 'POST',
        body: JSON.stringify(inviteData),
      });
      return handle(resp);
    },
    removeTeamMember: async (projectId, userId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/team-members/${userId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    updateMemberRole: async (projectId, userId, role) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${projectId}/team-members/${userId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ role }),
      });
      return handle(resp);
    },
    create: async (organizationId, data) => {
      console.log('🔧 Creating project with organizationId:', organizationId, 'data:', data);
      const headers = getAuthHeaders();
      const url = new URL(`${API_BASE_URL}/api/v1/projects`);
      if (organizationId) {
        url.searchParams.append('organization_id', organizationId);
      }
      console.log('🔧 Project creation URL:', url.toString());
      const resp = await fetch(url.toString(), {
        method: 'POST',
        headers,
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    update: async (id, data) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    delete: async (id) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/projects/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
  },

  // Boards
  boards: {
    getByProject: async (projectId) => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/projects/${projectId}/boards`,
        { headers: getAuthHeaders() }
      );
      const r = await handle(resp);
      return r.data || r; // handle both wrapped and unwrapped responses
    },
    getById: async (id) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/boards/${id}`, {
        headers: getAuthHeaders(),
      });
      const r = await handle(resp);
      return r.data;
    },
    create: async (projectId, data) => {
      // Backend expects POST /api/v1/projects/{projectId}/boards
      const payload = { name: data.name, description: data.description };
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/projects/${projectId}/boards`,
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: JSON.stringify(payload),
        }
      );
      return handle(resp);
    },
    update: async (id, data) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/boards/${id}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    delete: async (id) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/boards/${id}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
  },

  // Columns
  columns: {
    getByBoard: async (boardId) => {
      console.log(`🔧 Fetching columns for board: ${boardId}`);
      console.log(`🔧 API URL: ${API_BASE_URL}/api/v1/boards/${boardId}/columns`);
      console.log(`🔧 Auth headers:`, getAuthHeaders());

      const resp = await fetch(
        `${API_BASE_URL}/api/v1/boards/${boardId}/columns`,
        { headers: getAuthHeaders() }
      );

      console.log(`🔧 Response status: ${resp.status}`);
      console.log(`🔧 Response headers:`, Object.fromEntries(resp.headers.entries()));

      if (!resp.ok) {
        const errorText = await resp.text();
        console.error(`❌ Failed to fetch columns: ${resp.status} - ${errorText}`);
        throw new Error(`Failed to fetch columns: ${resp.status} - ${errorText}`);
      }

      const r = await handle(resp);
      console.log(`🔧 Columns response:`, r);

      // Ensure we return an array
      const columns = r.data || r || [];
      console.log(`✅ Processed columns:`, columns);

      return Array.isArray(columns) ? columns : [];
    },
    getById: async (id) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/columns/${id}`);
      const r = await handle(resp);
      return r.data;
    },
    create: async (boardId, data) => {
      // Backend expects POST /api/v1/columns with board_id in body
      const payload = {
        board_id: boardId,
        name: data.name,
        position: data.position,
        color: data.color,
      };
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/columns`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      return handle(resp);
    },
    update: async (id, data) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/columns/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    delete: async (id) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/columns/${id}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },
  },

  // Cards
  cards: {
    getAll: async (columnId = null) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/cards/${buildQS(
          columnId ? { column_id: columnId } : {}
        )}`
      );
      const r = await handle(resp);
      return r.data ? r.data : [];
    },
    getByProject: async (projectId, opts = {}) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/projects/${projectId}/cards`,
        {
          signal: opts.signal  // Support AbortController
        }
      );
      const r = await handle(resp);
      return r.data ? r.data : r;
    },
    getById: async (id) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${id}`);
      const result = await handle(resp);

      // Ensure consistent response format
      if (result && typeof result === 'object') {
        // If backend returns { success: true, data: {...} }, extract the data
        if (result.data) {
          return { data: result.data };
        }
        // If backend returns the card data directly, wrap it
        return { data: result };
      }

      return result;
    },
    create: async (columnId, data) => {
      const payload = { ...data, column_id: data.column_id || columnId };
      console.log('🔧 Creating card via realApiService:', payload);
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/column`, {
        method: 'POST',
        body: JSON.stringify(payload),
      });
      const result = await handle(resp);
      console.log('🔧 Card creation API response:', result);

      // Ensure consistent response format - backend returns { success, data, message }
      // Frontend expects { data: ... }
      if (result && typeof result === 'object') {
        // If backend returns { success: true, data: {...} }, extract the data
        if (result.data) {
          console.log('✅ Card created successfully via realApiService:', result.data);
          return { data: result.data };
        }
        // If backend returns the card data directly, wrap it
        console.log('✅ Card created successfully (direct format):', result);
        return { data: result };
      }

      console.log('⚠️ Unexpected response format:', result);
      return result;
    },
    update: async (id, data) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${id}`, {
        method: 'PUT',
        body: JSON.stringify(data),
      });

      // Enhanced error handling
      if (!resp.ok) {
        const errorText = await resp.text();
        console.error(`Card update failed (${resp.status}):`, errorText);
        throw new Error(`HTTP ${resp.status}: ${errorText}`);
      }

      return handle(resp);
    },
    delete: async (id) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${id}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },
    move: async (cardId, targetColumnId, position = null) => {
      const payload = { target_column_id: targetColumnId, position };
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/move`, {
        method: 'PUT', // Backend expects PUT, not POST
        body: JSON.stringify(payload),
      });
      return handle(resp);
    },
    getAssignableMembers: async (cardId) => {
      console.log('🔍 Fetching assignable members for card:', cardId);
      console.log('🔗 API URL:', `${API_BASE_URL}/api/v1/cards/${cardId}/assignable-members`);

      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/assignable-members`);

      console.log('📡 Response status:', resp.status);
      const result = await handle(resp);
      console.log('📋 API Result:', result);

      return result;
    },
    assignUser: async (cardId, userId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/assign?user_id=${userId}`, {
        method: 'POST',
      });
      return handle(resp);
    },
    unassignUser: async (cardId, userId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/assign/${userId}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },
    addComment: async (cardId, commentData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/comments`, {
        method: 'POST',
        body: JSON.stringify(commentData),
      });
      return handle(resp);
    },
    getMentionAutocomplete: async (cardId, search = '') => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/mention-autocomplete?search=${encodeURIComponent(search)}`);
      const result = await handle(resp);
      // Normalize into { success, data }
      let list = [];
      if (Array.isArray(result)) list = result;
      else if (Array.isArray(result?.data)) list = result.data;
      // Map to expected fields
      const mapped = list.map(m => ({
        id: m.id || m.user_id || m.uid,
        name: m.name || m.full_name || `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email?.split('@')[0] || 'User',
        primary_username: m.username || m.handle || m.email?.split('@')[0] || (m.name || '').toLowerCase().replace(/\s+/g, ''),
        email: m.email || m.user_email || '',
        avatar: m.avatar || m.profile_picture || null,
      }));
      return { success: true, data: mapped };
    },

    getCardActivities: async (cardId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/activities`);
      return handle(resp);
    },

    addCommentToCard: async (cardId, commentData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${cardId}/comments`, {
        method: 'POST',
        body: JSON.stringify(commentData),
      });
      return handle(resp);
    },
    getStatusValues: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/status-values`);
      return handle(resp);
    },
    getByAssignedUser: async (userId, params = {}) => {
      const qs = buildQS({ ...params, assigned_to: userId });
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/cards/${qs}`);
      const r = await handle(resp);
      return r.data || r || [];
    },
  },

  // Checklist
  checklist: {
    createBulk: async (cardId, checklistData) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/checklist/${cardId}/bulk`,
        {
          method: 'POST',
          body: JSON.stringify(checklistData),
        }
      );
      return handle(resp);
    },
    updateItem: async (itemId, update) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/checklist/items/${itemId}`,
        {
          method: 'PUT',
          body: JSON.stringify(update),
        }
      );
      return handle(resp);
    },
    deleteItem: async (itemId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/checklist/items/${itemId}`,
        { method: 'DELETE' }
      );
      return handle(resp);
    },
    generateAI: async (cardId, aiData) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/checklist/${cardId}/generate-ai`,
        {
          method: 'POST',
          body: JSON.stringify(aiData),
        }
      );
      return handle(resp);
    },
    getByCard: async (cardId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/checklist/cards/${cardId}/checklist`
      );
      return handle(resp);
    },
  },

  // Files
  files: {
    upload: async (file, cardId = null) => {
      const form = new FormData();
      form.append('file', file);
      if (cardId) form.append('card_id', cardId);
      const token = sessionService.getSessionToken?.(); // No localStorage fallback
      const resp = await fetch(`${API_BASE_URL}/api/v1/files/upload`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
      });
      return handle(resp);
    },
    getByCard: async (cardId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/files${buildQS({ card_id: cardId })}`
      );
      return handle(resp);
    },
    delete: async (fileId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/files/${fileId}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },
  },

  // Teams
  teams: {
    getMemberActivity: async (organizationId, userId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/teams/${organizationId}/members/${userId}/activity`
      );
      return handle(resp);
    },
    getTeamStats: async (organizationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/teams/${organizationId}/stats`
      );
      return handle(resp);
    },
    updateMemberRole: async (organizationId, userId, roleData) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/members/${userId}/role`,
        {
          method: 'PUT',
          body: JSON.stringify(roleData),
        }
      );
      return handle(resp);
    },
    removeMember: async (organizationId, userId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/members/${userId}`,
        { method: 'DELETE' }
      );
      return handle(resp);
    },
    // Organization Settings
    getSettings: async (organizationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`
      );
      return handle(resp);
    },
    updateSettings: async (organizationId, settingsData) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`,
        {
          method: 'PUT',
          body: JSON.stringify(settingsData),
        }
      );
      return handle(resp);
    },
    createSettings: async (organizationId, settingsData) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`,
        {
          method: 'POST',
          body: JSON.stringify(settingsData),
        }
      );
      return handle(resp);
    },

    // Recent activities for organization dashboard
    getActivities: async (organizationId, { limit = 20 } = {}) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/organizations/${organizationId}/activities`
      );
      const result = await handle(resp);
      // Backend returns an array directly; normalize to array
      if (Array.isArray(result)) {
        return limit ? result.slice(0, limit) : result;
      }
      if (Array.isArray(result?.data)) {
        return limit ? result.data.slice(0, limit) : result.data;
      }
      return [];
    },
  },

  // Notifications
  notifications: {
    getAll: async (params = {}) => {
      // Map frontend-friendly params to backend contract
      const mapped = new URLSearchParams();
      const unreadOnly = params.unread_only ?? params.unread ?? false;
      const limit = params.limit ?? 50;
      const skip = params.skip ?? 0;
      if (unreadOnly) mapped.append('unread_only', 'true');
      if (params.notification_type) mapped.append('notification_type', params.notification_type);
      if (skip) mapped.append('skip', String(skip));
      if (limit) mapped.append('limit', String(limit));
      const qs = mapped.toString();
      const url = qs
        ? `${API_BASE_URL}/api/v1/notifications/?${qs}`
        : `${API_BASE_URL}/api/v1/notifications/`;
      const resp = await fetchWithCredentials(url);
      const result = await handle(resp);
      
      // FIX: Ensure notifications is always an array
      if (result && typeof result === 'object') {
        if (Array.isArray(result)) {
          return result;
        }
        if (Array.isArray(result.data)) {
          return result.data;
        }
        if (Array.isArray(result.notifications)) {
          return result.notifications;
        }
        // If we get an object but no array, return empty array
        return [];
      }
      
      return [];
    },
    create: async (data) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/notifications/`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      return handle(resp);
    },
    markAsRead: async (notificationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/${notificationId}/read`,
        { method: 'PUT' }
      );
      return handle(resp);
    },
    markAllAsRead: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/mark-all-read`,
        { method: 'PUT' }
      );
      return handle(resp);
    },
    getStats: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/notifications/stats`);
      return handle(resp);
    },
    getStatistics: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/notifications/in-app/statistics`);
      const result = await handle(resp);

      // Handle the backend response format
      if (result && result.success && result.statistics) {
        return {
          success: true,
          data: result.statistics
        };
      }

      return {
        success: false,
        error: 'No statistics available'
      };
    },
    delete: async (notificationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/${notificationId}`,
        { method: 'DELETE' }
      );
      return handle(resp);
    },
    deleteInApp: async (notificationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/${notificationId}`,
        { method: 'DELETE' }
      );
      return handle(resp);
    },
    // In-app notification endpoints
    getUserNotifications: async (params = {}) => {
      const qs = buildQS(params);
      const url = qs
        ? `${API_BASE_URL}/api/v1/notifications/in-app/user-notifications${qs}`
        : `${API_BASE_URL}/api/v1/notifications/in-app/user-notifications`;
      const resp = await fetchWithCredentials(url);
      const result = await handle(resp);

      // Handle the backend response format
      if (result && result.success && Array.isArray(result.notifications)) {
        return {
          success: true,
          data: result.notifications,
          count: result.count
        };
      }

      return {
        success: false,
        data: [],
        count: 0
      };
    },
    createInApp: async (notification) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/create`,
        {
          method: 'POST',
          body: JSON.stringify(notification),
        }
      );
      return handle(resp);
    },
    sendWelcome: async (data) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/welcome`,
        {
          method: 'POST',
          body: JSON.stringify(data || {}),
        }
      );
      return handle(resp);
    },
    getUnreadCount: async (organizationId = null) => {
      const params = organizationId ? `?organization_id=${organizationId}` : '';
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/unread-count${params}`
      );
      const result = await handle(resp);

      // Handle the backend response format
      if (result && result.success && typeof result.unread_count === 'number') {
        return {
          success: true,
          count: result.unread_count
        };
      }

      return {
        success: false,
        count: 0
      };
    },
    markInAppAsRead: async (notificationId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/${notificationId}/read`,
        { method: 'PUT' }
      );
      return handle(resp);
    },
    markAllInAppAsRead: async (organizationId = null) => {
      const body = organizationId ? { organization_id: organizationId } : {};
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/notifications/in-app/mark-all-read`,
        {
          method: 'PUT',
          body: JSON.stringify(body)
        }
      );
      return handle(resp);
    },
    // Notification Preferences
    getPreferences: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/users/notifications/preferences`
      );
      return handle(resp);
    },
    updatePreferences: async (preferences) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/users/notifications/preferences`,
        {
          method: 'PUT',
          body: JSON.stringify(preferences),
        }
      );
      return handle(resp);
    },
  },

  // Billing
  billing: {
    getSubscription: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/subscription`
      );
      return handle(resp);
    },
    getInvoices: async ({ limit = 50, offset = 0, status } = {}) => {
      const qs = buildQS({ limit, offset, ...(status ? { status } : {}) });
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/invoices${qs}`
      );
      return handle(resp);
    },
    getPaymentHistory: async ({ limit = 50, offset = 0 } = {}) => {
      const qs = buildQS({ limit, offset });
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/payment-history${qs}`
      );
      return handle(resp);
    },
    getPlans: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/plans`
      );
      return handle(resp);
    },
    getDashboard: async () => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/dashboard`
      );
      return handle(resp);
    },
    generateInvoicePDF: async (invoiceId) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/invoices/${invoiceId}/generate-pdf`,
        { method: 'POST' }
      );
      return handle(resp);
    },
    downloadInvoicePDF: async (invoiceId) => {
      // Return a Blob for download
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/billing/invoices/${invoiceId}/download`
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const blob = await resp.blob();
      return blob;
    },
  },

  // AI Projects
  aiProjects: {
    preview: async ({
      name,
      project_type = 'general',
      team_size = 5,
      team_experience = 'intermediate',
      organization_id,
    }) => {
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/ai-projects/ai-preview`,
        {
          method: 'POST',
          body: JSON.stringify({
            name,
            project_type,
            team_size,
            team_experience,
            organization_id,
          }),
        }
      );
      return handle(resp);
    },
    createAIProject: async (organizationId, data) => {
      // Map incoming data to backend simple create contract
      const payload = {
        name:
          data.name ||
          data.overview?.title ||
          data.configuration?.name ||
          'Untitled Project',
        description:
          data.description ||
          data.overview?.description ||
          data.configuration?.description ||
          '',
        organization_id: data.organization_id || organizationId,
        generated_tasks: data.generated_tasks || data.tasks || [],
        configuration: data.configuration || {},
        tech_stack: data.tech_stack || data.techStack || {},
        workflow: data.workflow || {},
        start_date: data.configuration?.startDate || data.startDate,
        end_date: data.configuration?.endDate || data.endDate,
      };
      const resp = await fetchWithCredentials(
        `${API_BASE_URL}/api/v1/ai-projects/ai-create-simple`,
        {
          method: 'POST',
          body: JSON.stringify(payload),
        }
      );
      return handle(resp);
    },
    createFromPreview: async ({ confirmation_data, final_tasks, workflow }) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/ai-projects/ai-create`, {
        method: 'POST',
        body: JSON.stringify({ confirmation_data, final_tasks, workflow }),
      });
      return handle(resp);
    },
  },

  // Add AI task to board card (helper)
  aiProjectsTaskToBoard: async (organizationId, projectId, taskData) => {
    const payload = {
      organization_id: organizationId,
      project_id: projectId,
      task_data: taskData,
    };
    const resp = await fetchWithCredentials(
      `${API_BASE_URL}/api/v1/ai-projects/ai-task-to-board`,
      {
        method: 'POST',
        body: JSON.stringify(payload),
      }
    );
    return handle(resp);
  },

  // AI Projects metadata
  aiProjectsMeta: {
    getTemplates: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ai-projects/templates`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getTechStacks: async () => {
      const resp = await fetch(
        `${API_BASE_URL}/api/v1/ai-projects/tech-stacks`,
        {
          headers: getAuthHeaders(),
        }
      );
      return handle(resp);
    },
  },

  // AI Info
  ai: {
    getModels: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ai/models`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getInsights: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/ai/insights`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
  },

  // Project Sign-off


  // Support endpoints
  support: {
    getTickets: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/tickets`, {
        headers: getAuthHeaders()
      });
      return handle(resp);
    },
    createTicket: async (ticketData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/tickets`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(ticketData)
      });
      return handle(resp);
    },
    getTicket: async (ticketId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/tickets/${ticketId}`, {
        headers: getAuthHeaders()
      });
      return handle(resp);
    },
    updateTicket: async (ticketId, updateData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/tickets/${ticketId}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(updateData)
      });
      return handle(resp);
    },
    sendContactMessage: async (messageData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders()
        },
        body: JSON.stringify(messageData)
      });
      return handle(resp);
    },
    getHelpArticles: async () => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/help-articles`, {
        headers: getAuthHeaders()
      });
      return handle(resp);
    },
    getHelpArticle: async (articleId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/support/help-articles/${articleId}`, {
        headers: getAuthHeaders()
      });
      return handle(resp);
    }
  },

  // Meetings Services
  meetings: {
    getCalendarConfig: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/meetings/calendar/config`);
      const r = await handle(resp);
      return r.data || r;
    },
    getAll: async (params = {}) => {
      const qs = new URLSearchParams(params).toString();
      const url = qs ? `${API_BASE_URL}/api/v1/meetings?${qs}` : `${API_BASE_URL}/api/v1/meetings`;
      const resp = await fetchWithCredentials(url);
      return handle(resp);
    },
    getById: async (meetingId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getByProject: async (projectId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/project/${projectId}`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    create: async (meetingData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/create`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(meetingData),
      });
      return handle(resp);
    },
    createInstant: async (meetingData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/instant`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(meetingData),
      });
      return handle(resp);
    },
    update: async (meetingId, updateData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      });
      return handle(resp);
    },
    delete: async (meetingId) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/${meetingId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    integrateCalendar: async (integrationData) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/meetings/calendar/integrate`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(integrationData),
      });
      return handle(resp);
    },
  },

  

  // Organization Settings Services
  organizationSettings: {
    get: async (organizationId) => {
      try {
        const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`, {
          headers: getAuthHeaders(),
        });
        return await handle(resp);
      } catch (e) {
        // If settings do not exist yet, create with sensible defaults
        if (e && e.status === 404) {
          const defaults = {
            allow_admin_create_projects: true,
            allow_member_create_projects: false,
            require_domain_match: true,
            enable_task_notifications: true,
            enable_role_change_notifications: true,
            require_email_verification: true,
          };
          const created = await realApiService.organizationSettings.create(organizationId, defaults);
          return created;
        }
        throw e;
      }
    },
    create: async (organizationId, settings) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      });
      return handle(resp);
    },
    update: async (organizationId, settings) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${organizationId}/settings`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(settings),
      });
      return handle(resp);
    },
    updateProjectCreationPermissions: async (organizationId, permissions) => {
      const resp = await fetch(`${API_BASE_URL}/api/v1/organizations/${organizationId}/settings/project-permissions`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(permissions),
      });
      return handle(resp);
    },
  },

  // Integrations Services
  integrations: {
    getAll: async (organizationId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getById: async (organizationId, integrationId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations/${integrationId}`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    create: async (organizationId, integrationData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(integrationData),
      });
      return handle(resp);
    },
    update: async (organizationId, integrationId, updateData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations/${integrationId}`, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(updateData),
      });
      return handle(resp);
    },
    delete: async (organizationId, integrationId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations/${integrationId}`, {
        method: 'DELETE',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    sync: async (organizationId, integrationId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations/${integrationId}/sync`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getLogs: async (organizationId, integrationId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/organizations/${organizationId}/integrations/${integrationId}/logs`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
    getTemplates: async () => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/integrations/integration-templates`, {
        headers: getAuthHeaders(),
      });
      return handle(resp);
    },
  },

  // Kanban Board Operations
  kanban: {
    getProjectBoard: async (projectId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/projects/${projectId}/board`);
      return handle(resp);
    },

    createColumn: async (boardId, columnData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/boards/${boardId}/columns`, {
        method: 'POST',
        body: JSON.stringify(columnData),
      });
      return handle(resp);
    },

    updateColumn: async (columnId, columnData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/columns/${columnId}`, {
        method: 'PUT',
        body: JSON.stringify(columnData),
      });
      return handle(resp);
    },

    deleteColumn: async (columnId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/columns/${columnId}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },

    createCard: async (columnId, cardData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/columns/${columnId}/cards`, {
        method: 'POST',
        body: JSON.stringify(cardData),
      });
      return handle(resp);
    },

    updateCard: async (cardId, cardData) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/cards/${cardId}`, {
        method: 'PUT',
        body: JSON.stringify(cardData),
      });
      return handle(resp);
    },

    deleteCard: async (cardId) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/cards/${cardId}`, {
        method: 'DELETE',
      });
      return handle(resp);
    },

    moveCard: async (cardId, targetColumnId, position = null) => {
      const resp = await fetchWithCredentials(`${API_BASE_URL}/api/v1/kanban/cards/${cardId}/move`, {
        method: 'PUT',
        body: JSON.stringify({
          target_column_id: targetColumnId,
          position: position
        }),
      });
      return handle(resp);
    }
  },
};

// Standardized checklist item update function (matches requirements)
export const updateChecklistItem = async (itemId, isCompleted) => {
  const resp = await fetch(`${API_BASE_URL}/api/v1/checklist/items/${itemId}`, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      ...getAuthHeaders(),
    },
    body: JSON.stringify({ is_completed: isCompleted }),
  });
  if (!resp.ok) throw new Error(await resp.text());
  return resp.json();
};



export default realApiService;